import React from 'react';
import { MessageEmoji, joinWithAnd } from 'shared';

import Text from 'ds/Text';
import { selectLoggedInUserId } from 'store/User/selectors';
import { useAppSelector } from 'store/hooks';

import Clickable from './Clickable';
import Layout from './Layout';
import Tooltip from './Tooltip';
import { namePartsToFirstLastInitial } from './utils';

interface MessageEmojisProps {
  emojis: MessageEmoji[];
  messageId: number;
  onEmojiSelect: ({ emoji, messageId }: { emoji: string; messageId: number }) => void;
}

const MessageEmojis: React.FC<MessageEmojisProps> = ({ emojis, messageId, onEmojiSelect }) => {
  const userId = useAppSelector(selectLoggedInUserId);

  const handleClick = async (value: string) => {
    onEmojiSelect({ emoji: value, messageId });
  };

  // Group emojis by value
  const groupedEmojis = emojis.reduce(
    (acc, emoji) => {
      acc[emoji.value] = (acc[emoji.value] || []).concat(emoji);
      return acc;
    },
    {} as Record<string, MessageEmoji[]>
  );

  return (
    <Layout wrap gap={8}>
      {Object.entries(groupedEmojis).map(([value, emojiGroup]) => {
        const userReactedEmoji = emojiGroup.find(e => e.user.id === userId);

        const userEmoji = emojiGroup.find(e => e.user.id === userId);
        const otherUsersEmojis = emojiGroup.filter(e => e.user.id !== userId);

        const emojiUserNames = otherUsersEmojis.map(e =>
          namePartsToFirstLastInitial({ firstname: e.user.first, lastname: e.user.last })
        );

        if (userEmoji) {
          emojiUserNames.push('You');
        }

        const tooltipText = joinWithAnd(emojiUserNames);

        return (
          <Tooltip text={tooltipText} placement="top" key={emojiGroup[0].id}>
            <Clickable onClick={() => handleClick(value)}>
              <Layout
                {...(userReactedEmoji
                  ? { color: 'blue-50', borderColor: 'blue-500', hoverColor: 'blue-100' }
                  : { color: 'gray-50', borderColor: 'gray-50' })}
                borderRadius={16}
                borderWidth={1}
                paddingX={12}
                paddingY={3}
                gap={8}
                align="center"
              >
                <Text size="body-sm" semibold>
                  {value}
                </Text>
                <Text size="body-xs" color="blue-500" semibold>
                  {emojiGroup.length}
                </Text>
              </Layout>
            </Clickable>
          </Tooltip>
        );
      })}
    </Layout>
  );
};

export default MessageEmojis;
